import { useState } from "react";
import dayjs from "dayjs";
import {
  ActionIcon,
  Badge,
  Button,
  CopyButton,
  Group,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconCheck,
  IconCircleCheckFilled,
  IconCopy,
  IconEye,
  IconEyeOff,
  IconLink,
  IconUserMinus,
} from "@tabler/icons-react";

import { webConfig } from "../../../config.js";
import { useUnclaimTicket } from "../../api/tickets";
import { formatClaimCode, getClaimAccount } from "../../utils/ticketClaim";
import {
  getAppStatus,
  confirmUnclaim,
  unclaimBody,
} from "../Tickets/ClaimStatus";

export { getAppStatus };

export const fullName = (ticket) =>
  [ticket?.firstName, ticket?.lastName].filter(Boolean).join(" ");

/** The link that opens the ticket screen with the code filled in. */
export const ticketLink = (ticket) =>
  ticket?.claimCode
    ? `${webConfig.baseUrl}/open/claim/${ticket.claimCode}`
    : null;

/**
 * Where a ticket is in the app, said for the organizer: a short label for the
 * list and one sentence for the ticket panel that ends in what to do, if
 * anything.
 */
export const STATUS = {
  notAdded: {
    label: "Not added",
    color: "gray",
    sentence: "Not in the app yet. Send the ticket or share the link.",
  },
  inApp: {
    label: "In app",
    color: "green",
    sentence: "In the app.",
  },
  guest: {
    label: "Guest",
    color: "yellow",
    sentence:
      "In the app as a guest, without email, Google or Apple. If they log out, only you can give the ticket back.",
  },
  lockedOut: {
    label: "Locked out",
    color: "red",
    sentence:
      "A guest who logged out and cannot get back in. Take the ticket out of the app; they can add it again with the same code or link.",
  },
};

export function StatusBadge({ ticket, ...props }) {
  const status = STATUS[getAppStatus(ticket)];
  return (
    <Badge color={status.color} variant="light" {...props}>
      {status.label}
    </Badge>
  );
}

/** Copies the ticket link, the one thing to hand over by chat or message. */
export function CopyLinkButton({ ticket, ...props }) {
  const link = ticketLink(ticket);
  if (!link) return null;
  return (
    <CopyButton value={link} timeout={1500}>
      {({ copied, copy }) => (
        <Button
          variant="light"
          size="xs"
          color={copied ? "green" : "blue"}
          leftSection={
            copied ? <IconCheck size={14} /> : <IconLink size={14} />
          }
          onClick={(e) => {
            e.stopPropagation();
            copy();
          }}
          {...props}
        >
          {copied ? "Link copied" : "Copy link"}
        </Button>
      )}
    </CopyButton>
  );
}

/** The ticket code, masked until revealed, for reading out over the phone. */
export function TicketCode({ ticket }) {
  const [isVisible, setIsVisible] = useState(false);
  const code = ticket?.claimCode;
  if (!code) return null;

  return (
    <Group gap={4} wrap="nowrap">
      <Text size="sm" ff="monospace" fw={500} style={{ letterSpacing: 1 }}>
        {isVisible ? formatClaimCode(code) : "•••• ••••"}
      </Text>
      <Tooltip label={isVisible ? "Hide code" : "Show code"}>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          aria-label={isVisible ? "Hide code" : "Show code"}
          onClick={() => setIsVisible((visible) => !visible)}
        >
          {isVisible ? <IconEyeOff size={16} /> : <IconEye size={16} />}
        </ActionIcon>
      </Tooltip>
      <CopyButton value={code} timeout={1500}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? "Copied" : "Copy code"}>
            <ActionIcon
              variant="subtle"
              color={copied ? "green" : "gray"}
              size="sm"
              aria-label="Copy code"
              onClick={copy}
            >
              {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  );
}

export function NameCell({ ticket }) {
  return (
    <div>
      <Text size="sm" fw={500}>
        {fullName(ticket) || "No name"}
      </Text>
      <Text size="xs" c="dimmed" lineClamp={1}>
        {ticket.email || "No email"}
      </Text>
    </div>
  );
}

export function CheckedInCell({ ticket }) {
  if (!ticket.checkedInAt) return null;
  const at = dayjs(ticket.checkedInAt);
  return (
    <Group gap={6} wrap="nowrap">
      <IconCircleCheckFilled size={16} color="var(--mantine-color-green-6)" />
      <Text size="sm">
        {at.isSame(dayjs(), "day") ? at.format("HH:mm") : at.format("DD.MM.")}
      </Text>
    </Group>
  );
}

export function TakeOutButton({ ticket }) {
  const { mutate: unclaim, isPending } = useUnclaimTicket({
    ticketId: ticket.id,
  });
  return (
    <Button
      variant="light"
      color="red"
      size="xs"
      leftSection={<IconUserMinus size={14} />}
      loading={isPending}
      onClick={(e) => {
        e.stopPropagation();
        confirmUnclaim({
          account: getClaimAccount(ticket),
          onConfirm: (newCode) => unclaim(unclaimBody(newCode)),
        });
      }}
    >
      Take out
    </Button>
  );
}
